// Glowy Mail — e-posta tasarım stüdyosu (Glowy Studio çerçeve editörünün e-posta karşılığı).
// Sol: blok ekle + blok listesi · orta: canlı önizleme (tıkla-seç) · sağ: seçili blok ya da tema/e-posta ayarları.
// Belge biçimi ve HTML derleyici: ./emailDoc.js. Kaydedince design + derlenmiş html şablona yazılır.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  X, Save, Loader2, Undo2, Redo2, Monitor, Smartphone, Plus, ChevronUp, ChevronDown, Copy, Trash2,
  Type, Image as ImageIcon, LayoutGrid, Columns2, ListOrdered, AlignLeft, MousePointerClick, Space, Search, Palette, Mail,
} from 'lucide-react';
import { btnPrimary, btnGhost, inputCls } from '../../ChatAssistant/ui';
import { mailApi, previewHtml } from '../api';
import {
  renderEmail, newBlock, BLOCK_TYPES, BG_OPTIONS, HEAD_FONTS, collectImages, usedTags, itemFromProduct, money, slugify, uid,
} from './emailDoc';
import { startersFor, docFromStarter } from './starters';

const ICONS = { hero: Type, image: ImageIcon, cover: ImageIcon, split: Columns2, products: LayoutGrid, compare: Columns2, steps: ListOrdered, text: AlignLeft, button: MousePointerClick, spacer: Space };
const SAMPLE = { first_name: 'Ayşe', last_name: 'Yılmaz', email: 'ayse@example.com' };
const clone = (o) => JSON.parse(JSON.stringify(o));

// ── küçük form parçaları ────────────────────────────────────────────────────
function Row({ label, children, hint }) {
  return (
    <label className="block mb-3">
      <span className="block text-[11px] font-medium text-gray-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

// Metin alanı: odaklanınca "son odak" olarak kaydolur → kişi alanı çipleri imleç konumuna yazar
function TextIn({ value, onChange, multiline, rows = 3, focusRef, placeholder }) {
  const ref = useRef(null);
  const onFocus = () => { focusRef.current = { el: ref.current, onChange }; };
  const props = { ref, className: inputCls, value: value ?? '', placeholder, onFocus, onChange: (e) => onChange(e.target.value) };
  return multiline ? <textarea {...props} rows={rows} /> : <input {...props} />;
}

function Seg({ value, options, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-2 py-1 text-[11px] rounded-md ${value === v ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-800'}`}>{l}</button>
      ))}
    </div>
  );
}

function BgPick({ value, onChange }) {
  const custom = value && value.startsWith('#');
  return (
    <div className="flex items-center gap-2">
      <Seg value={custom ? 'custom' : (value || 'white')} options={[...BG_OPTIONS, ['custom', 'Color']]} onChange={(v) => onChange(v === 'custom' ? '#a50044' : v)} />
      {custom && <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-7 h-7 rounded border border-gray-200" />}
    </div>
  );
}

function ColorRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] text-gray-600">{label}</span>
      <div className="flex items-center gap-1.5">
        <input className="w-20 rounded-md border border-gray-200 px-1.5 py-0.5 text-[11px] font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'} onChange={(e) => onChange(e.target.value)} className="w-6 h-6 rounded border border-gray-200" />
      </div>
    </div>
  );
}

function LinkList({ items, onChange, focusRef }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-1.5">
      {items.map((n, i) => (
        <div key={i} className="flex gap-1">
          <input className={`${inputCls} !w-24`} value={n.label} placeholder="Label" onChange={(e) => set(i, 'label', e.target.value)} />
          <input className={inputCls} value={n.url} placeholder="https://" onChange={(e) => set(i, 'url', e.target.value)} />
          <button type="button" className="text-gray-300 hover:text-red-600 px-1" onClick={() => onChange(items.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={() => onChange([...items, { label: '', url: '' }])}>+ Add link</button>
    </div>
  );
}

// Seçili ürünün küçük kartı (değiştir / kaldır)
function ItemChip({ item, onPick, onClear, label = 'Product' }) {
  if (!item) {
    return <button type="button" className={`${btnGhost} w-full`} onClick={onPick}><Search />Pick {label.toLowerCase()}</button>;
  }
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-1.5">
      <img src={item.jpg || item.image} alt="" className="w-10 h-10 object-cover bg-gray-100 rounded" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium text-gray-800 truncate">{item.title}</div>
        <div className="text-[10px] text-gray-500 truncate">{item.color ? `${item.color} · ` : ''}{money(item.price)}</div>
      </div>
      <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={onPick}>Change</button>
      {onClear && <button type="button" className="text-gray-300 hover:text-red-600" onClick={onClear}><X className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

// ── ürün seçici ─────────────────────────────────────────────────────────────
function ProductPicker({ brandId, onPick, onClose }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await mailApi(brandId, '/products', { query: { q, limit: 60 } });
        if (alive) { setList(r.products); setErr(r.source ? null : 'No product catalog for this brand yet (Merchant Center feed not connected).'); }
      } catch (e) { if (alive) setErr(e.message); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [brandId, q]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search className="w-4 h-4 text-gray-400" />
          <input autoFocus className="flex-1 text-sm outline-none" placeholder="Search products (name or brand)…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-4">
          {err && <div className="text-xs text-amber-700 mb-3">{err}</div>}
          {list === null ? (
            <div className="py-16 text-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {list.map((p) => (
                <button key={p.id} type="button" onClick={() => onPick(itemFromProduct(p))}
                  className="text-left rounded-lg border border-gray-200 hover:border-indigo-400 overflow-hidden">
                  <div className="aspect-square bg-gray-50"><img src={p.image} alt="" loading="lazy" className="w-full h-full object-contain" /></div>
                  <div className="p-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-400">{p.brand}</div>
                    <div className="text-[11px] text-gray-800 line-clamp-2 leading-snug">{p.title}</div>
                    <div className="text-[11px] mt-1"><b>{money(p.price)}</b>{p.listPrice ? <span className="text-gray-400 line-through ml-1">{money(p.listPrice)}</span> : null}</div>
                    {!p.inStock && <div className="text-[10px] text-red-600 mt-0.5">Out of stock</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── başlangıç seçici ────────────────────────────────────────────────────────
function Thumb({ html }) {
  return (
    <div className="h-56 overflow-hidden bg-gray-100 relative pointer-events-none">
      <iframe title="" sandbox="" srcDoc={html} className="border-0 bg-white origin-top-left" style={{ width: 680, height: 900, transform: 'scale(0.4)' }} />
    </div>
  );
}

function StarterPicker({ brandId, theme, onPick, onClose }) {
  const starters = useMemo(() => startersFor(brandId).map((s) => {
    const d = docFromStarter(s, brandId);
    if (theme && s.key === 'blank') d.theme = clone(theme);
    return { s, html: previewHtml(renderEmail(d, {}), SAMPLE) };
  }), [brandId, theme]);
  return (
    <div className="fixed inset-0 z-[70] bg-gray-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New email design</h2>
            <p className="text-xs text-gray-500 mt-0.5">Start blank or from one of the brand's designs. Everything is editable.</p>
          </div>
          <button className={btnGhost} onClick={onClose}><X />Close</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {starters.map(({ s, html }) => (
            <button key={s.key} type="button" onClick={() => onPick(s)} className="text-left bg-white rounded-xl border border-gray-200 hover:border-indigo-400 overflow-hidden">
              <Thumb html={html} />
              <div className="px-3 py-2.5 border-t border-gray-100">
                <div className="text-sm font-medium text-gray-900 truncate">{s.key === 'blank' ? 'Blank' : s.subject}</div>
                <div className="text-[11px] text-gray-500 truncate">{s.group}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── blok denetçisi ──────────────────────────────────────────────────────────
function BlockInspector({ b, set, pick, focusRef }) {
  const T = (k, multi, rows) => <TextIn value={b[k]} onChange={(v) => set({ [k]: v })} multiline={multi} rows={rows} focusRef={focusRef} />;
  switch (b.type) {
    case 'hero':
      return (<>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Eyebrow">{T('eyebrow')}</Row>
        <Row label="Title" hint="New line = line break">{T('title', true, 2)}</Row>
        <Row label={`Title size · ${b.titleSize}px`}><input type="range" min="28" max="72" value={b.titleSize || 46} onChange={(e) => set({ titleSize: +e.target.value })} className="w-full" /></Row>
        <Row label="Text" hint="**bold** for emphasis">{T('text', true, 4)}</Row>
        <Row label="Button label" hint="Leave empty for no button"><TextIn value={b.button?.label} onChange={(v) => set({ button: { ...b.button, label: v } })} focusRef={focusRef} /></Row>
        <Row label="Button link"><input className={inputCls} value={b.button?.url || ''} placeholder="https://" onChange={(e) => set({ button: { ...b.button, url: e.target.value } })} /></Row>
      </>);
    case 'image':
      return (<>
        <Row label="Product image"><ItemChip item={b.item} onPick={() => pick((it) => set({ item: it }))} onClear={b.item ? () => set({ item: null }) : null} /></Row>
        {!b.item && <Row label="…or image URL"><input className={inputCls} value={b.src || ''} placeholder="https://" onChange={(e) => set({ src: e.target.value, jpg: null, jpgWide: null })} /></Row>}
        <Row label="Shape"><Seg value={b.crop} options={[['wide', 'Wide 3:2'], ['square', 'Square']]} onChange={(v) => set({ crop: v })} /></Row>
        <Row label="Link" hint="Empty = product page"><input className={inputCls} value={b.url || ''} placeholder="https://" onChange={(e) => set({ url: e.target.value })} /></Row>
      </>);
    case 'cover':
    case 'split': {
      const pic = (<>
        <Row label="Photo"><ItemChip item={b.item} label="Product photo" onPick={() => pick((it) => set({ item: it }))} onClear={b.item ? () => set({ item: null }) : null} /></Row>
        {!b.item && <Row label="…or image URL" hint="Campaign / lifestyle photo"><input className={inputCls} value={b.src || ''} placeholder="https://" onChange={(e) => set({ src: e.target.value, jpg: null })} /></Row>}
      </>);
      return (<>
        {pic}
        {b.type === 'split' && <Row label="Photo side"><Seg value={b.side || 'left'} options={[['left', 'Left'], ['right', 'Right']]} onChange={(v) => set({ side: v })} /></Row>}
        <Row label="Text background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Eyebrow">{T('eyebrow')}</Row>
        <Row label="Title" hint="New line = line break">{T('title', true, 2)}</Row>
        <Row label={`Title size · ${b.titleSize || (b.type === 'cover' ? 34 : 24)}px`}><input type="range" min="18" max="56" value={b.titleSize || (b.type === 'cover' ? 34 : 24)} onChange={(e) => set({ titleSize: +e.target.value })} className="w-full" /></Row>
        <Row label="Text" hint="**bold** for emphasis">{T('text', true, 4)}</Row>
        {b.type === 'cover' && <Row label="Align"><Seg value={b.align || 'center'} options={[['center', 'Center'], ['left', 'Left']]} onChange={(v) => set({ align: v })} /></Row>}
        <Row label="Link label" hint="Empty = no link"><TextIn value={b.link?.label} onChange={(v) => set({ link: { ...b.link, label: v } })} focusRef={focusRef} /></Row>
        <Row label="Link" hint="Empty = product page"><input className={inputCls} value={b.link?.url || ''} placeholder="https://" onChange={(e) => set({ link: { ...b.link, url: e.target.value } })} /></Row>
      </>);
    }
    case 'products':
      return (<>
        <Row label="Layout"><Seg value={b.layout || 'grid'} options={[['grid', 'Grid'], ['editorial', 'Editorial']]} onChange={(v) => set({ layout: v })} /></Row>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Title">{T('title')}</Row>
        <Row label={b.layout === 'editorial' ? 'Link label' : 'Buy button'} hint="Empty = none">{T('buyLabel')}</Row>
        <div className="text-[11px] font-medium text-gray-600 mb-1">Products ({(b.items || []).length})</div>
        <div className="space-y-2 mb-2">
          {(b.items || []).map((it, i) => (
            <div key={i} className="rounded-lg bg-gray-50 p-1.5 space-y-1">
              <ItemChip item={it.item}
                onPick={() => pick((item) => set({ items: b.items.map((x, j) => (j === i ? { ...x, item } : x)) }))}
                onClear={() => set({ items: b.items.filter((_, j) => j !== i) })} />
              <div className="flex gap-1 items-center">
                <TextIn value={it.label} placeholder="Label above (optional)" focusRef={focusRef} onChange={(v) => set({ items: b.items.map((x, j) => (j === i ? { ...x, label: v } : x)) })} />
                <button type="button" className="text-gray-400 hover:text-gray-800" disabled={i === 0} onClick={() => { const a = [...b.items]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; set({ items: a }); }}><ChevronUp className="w-3.5 h-3.5" /></button>
                <button type="button" className="text-gray-400 hover:text-gray-800" disabled={i === b.items.length - 1} onClick={() => { const a = [...b.items]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; set({ items: a }); }}><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" className={`${btnGhost} w-full`} onClick={() => pick((item) => set({ items: [...(b.items || []), { item, label: '' }] }))}><Plus />Add product</button>
      </>);
    case 'compare':
      return (<>
        <Row label="Left product"><ItemChip item={b.a} onPick={() => pick((it) => set({ a: it }))} /></Row>
        <Row label="Left label">{T('labelA')}</Row>
        <Row label="Right product"><ItemChip item={b.b} onPick={() => pick((it) => set({ b: it }))} /></Row>
        <Row label="Right label">{T('labelB')}</Row>
        <Row label="Buy button" hint="Empty = no button">{T('buyLabel')}</Row>
        <div className="text-[11px] font-medium text-gray-600 mb-1">Comparison rows</div>
        <div className="space-y-1 mb-2">
          {(b.rows || []).map((r, i) => (
            <div key={i} className="flex gap-1">
              {[0, 1, 2].map((k) => (
                <input key={k} className={`${inputCls} !px-1.5 !text-[11px]`} value={r[k] || ''} placeholder={['Row', 'Left', 'Right'][k]}
                  onChange={(e) => set({ rows: b.rows.map((x, j) => (j === i ? x.map((y, m) => (m === k ? e.target.value : y)) : x)) })} />
              ))}
              <button type="button" className="text-gray-300 hover:text-red-600" onClick={() => set({ rows: b.rows.filter((_, j) => j !== i) })}><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={() => set({ rows: [...(b.rows || []), ['', '', '']] })}>+ Add row</button>
          {b.a && b.b && <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={() => set({ rows: [...(b.rows || []), ['Bugünkü fiyat', money(b.a.price), money(b.b.price)]] })}>+ Price row</button>}
        </div>
      </>);
    case 'steps':
      return (<>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Title">{T('title')}</Row>
        <div className="space-y-2 mb-2">
          {(b.items || []).map((s, i) => (
            <div key={i} className="rounded-lg bg-gray-50 p-1.5 space-y-1">
              <div className="flex gap-1 items-center">
                <span className="text-[11px] font-semibold text-gray-400 w-5">{String(i + 1).padStart(2, '0')}</span>
                <TextIn value={s.title} focusRef={focusRef} onChange={(v) => set({ items: b.items.map((x, j) => (j === i ? { ...x, title: v } : x)) })} />
                <button type="button" className="text-gray-300 hover:text-red-600" onClick={() => set({ items: b.items.filter((_, j) => j !== i) })}><X className="w-3.5 h-3.5" /></button>
              </div>
              <TextIn value={s.text} multiline rows={2} focusRef={focusRef} onChange={(v) => set({ items: b.items.map((x, j) => (j === i ? { ...x, text: v } : x)) })} />
            </div>
          ))}
        </div>
        <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={() => set({ items: [...(b.items || []), { title: '', text: '' }] })}>+ Add step</button>
      </>);
    case 'text':
      return (<>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Text" hint="**bold** for emphasis">{T('text', true, 6)}</Row>
        <Row label="Style"><Seg value={b.small ? 'small' : 'body'} options={[['body', 'Body'], ['small', 'Small note']]} onChange={(v) => set({ small: v === 'small' })} /></Row>
      </>);
    case 'button':
      return (<>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label="Label">{T('label')}</Row>
        <Row label="Link"><input className={inputCls} value={b.url || ''} placeholder="https://" onChange={(e) => set({ url: e.target.value })} /></Row>
      </>);
    case 'spacer':
      return (<>
        <Row label="Background"><BgPick value={b.bg} onChange={(v) => set({ bg: v })} /></Row>
        <Row label={`Height · ${b.height}px`}><input type="range" min="8" max="80" value={b.height || 24} onChange={(e) => set({ height: +e.target.value })} className="w-full" /></Row>
      </>);
    default: return null;
  }
}

function ThemeInspector({ t, set, focusRef }) {
  return (<>
    <Row label="Logo (header)" hint="PNG/JPG URL, transparent background works best"><input className={inputCls} value={t.logo || ''} onChange={(e) => set({ logo: e.target.value })} /></Row>
    <Row label="Logo (footer, light version)"><input className={inputCls} value={t.logoFooter || ''} onChange={(e) => set({ logoFooter: e.target.value })} /></Row>
    <Row label={`Logo width · ${t.logoWidth}px`}><input type="range" min="48" max="200" value={t.logoWidth} onChange={(e) => set({ logoWidth: +e.target.value })} className="w-full" /></Row>
    <Row label="Home link"><input className={inputCls} value={t.homeUrl || ''} placeholder="https://" onChange={(e) => set({ homeUrl: e.target.value })} /></Row>
    <div className="text-[11px] font-medium text-gray-600 mb-1.5 mt-4">Colors</div>
    <ColorRow label="Ink (text, footer)" value={t.ink} onChange={(v) => set({ ink: v })} />
    <ColorRow label="Buttons" value={t.accent} onChange={(v) => set({ accent: v })} />
    <ColorRow label="Soft background" value={t.soft} onChange={(v) => set({ soft: v })} />
    <ColorRow label="Muted text" value={t.muted} onChange={(v) => set({ muted: v })} />
    <Row label="Corners"><Seg value={t.radius ? 'round' : 'square'} options={[['square', 'Square'], ['round', 'Rounded']]} onChange={(v) => set({ radius: v === 'round' ? 12 : 0 })} /></Row>
    <Row label="Heading font">
      <select className={inputCls} value={t.headFont} onChange={(e) => set({ headFont: e.target.value })}>
        {Object.keys(HEAD_FONTS).map((f) => <option key={f}>{f}</option>)}
      </select>
    </Row>
    <Row label="Headings"><Seg value={t.uppercaseHeadings ? 'upper' : 'normal'} options={[['upper', 'UPPERCASE'], ['normal', 'As typed']]} onChange={(v) => set({ uppercaseHeadings: v === 'upper' })} /></Row>
    <Row label={`Heading letter spacing · ${t.headTracking ?? 'default'}`}><input type="range" min="0" max="8" step="0.5" value={t.headTracking ?? 0} onChange={(e) => set({ headTracking: +e.target.value })} className="w-full" /></Row>
    <Row label="Buttons"><Seg value={t.buttonStyle || 'solid'} options={[['solid', 'Solid'], ['outline', 'Outline'], ['link', 'Text link']]} onChange={(v) => set({ buttonStyle: v })} /></Row>
    <Row label="Footer"><Seg value={t.footerStyle || 'dark'} options={[['dark', 'Dark'], ['light', 'Light']]} onChange={(v) => set({ footerStyle: v })} /></Row>
    <div className="text-[11px] font-medium text-gray-600 mb-1.5 mt-4">Header menu</div>
    <LinkList items={t.nav || []} onChange={(nav) => set({ nav })} />
    <div className="text-[11px] font-medium text-gray-600 mb-1.5 mt-4">Footer</div>
    <Row label="Footer text"><TextIn value={t.footerText} multiline rows={2} focusRef={focusRef} onChange={(v) => set({ footerText: v })} /></Row>
    <LinkList items={t.footerLinks || []} onChange={(footerLinks) => set({ footerLinks })} />
    <Row label="UTM source" hint="Added to every link with utm_medium=email and the template name as campaign"><input className={`${inputCls} mt-3`} value={t.utmSource || ''} onChange={(e) => set({ utmSource: e.target.value })} /></Row>
  </>);
}

function blockSummary(b) {
  const s = b.title || b.text || b.label || b.item?.title || b.a?.title || (b.items || []).map((i) => i.item?.title).filter(Boolean)[0] || '';
  return String(s).replace(/\{\{([^|}]+)\|?([^}]*)\}\}/g, (m, k, fb) => fb || k).replace(/\n/g, ' ');
}

// ── stüdyo ──────────────────────────────────────────────────────────────────
export default function EmailStudio({ brandId, initial, lastTheme, onClose, onSaved }) {
  const [meta, setMeta] = useState({ id: initial.id || null, name: initial.name || '', subject: initial.subject || '', preheader: initial.preheader || '' });
  const [doc, setDoc] = useState(initial.design || null);
  const [sel, setSel] = useState(null);
  const [panel, setPanel] = useState('block'); // block | theme | email
  const [device, setDevice] = useState('desktop');
  const [picker, setPicker] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [customTag, setCustomTag] = useState('');
  const hist = useRef({ past: [], future: [] });
  const focusRef = useRef(null);
  const frame = useRef(null);
  const scrollY = useRef(0);

  // Belge değişikliği: geçmişe yaz (yazarken her harf ayrı adım olmasın diye 600 ms birleştir)
  // Geçmiş setDoc güncelleyicisinin dışında tutulur (StrictMode güncelleyiciyi iki kez çağırır)
  const lastPush = useRef(0);
  const docRef = useRef(doc);
  const put = useCallback((n) => { docRef.current = n; setDoc(n); }, []);
  useEffect(() => { docRef.current = doc; }, [doc]);
  const change = useCallback((fn) => {
    const now = Date.now();
    if (now - lastPush.current > 600) {
      hist.current.past.push(JSON.stringify(docRef.current));
      if (hist.current.past.length > 60) hist.current.past.shift();
      hist.current.future = [];
    }
    lastPush.current = now;
    const n = clone(docRef.current);
    fn(n);
    put(n);
    setDirty(true);
  }, [put]);
  const undo = useCallback(() => {
    const p = hist.current.past.pop();
    if (!p) return;
    hist.current.future.push(JSON.stringify(docRef.current));
    put(JSON.parse(p));
    lastPush.current = 0;
    setDirty(true);
  }, [put]);
  const redo = useCallback(() => {
    const f = hist.current.future.pop();
    if (!f) return;
    hist.current.past.push(JSON.stringify(docRef.current));
    put(JSON.parse(f));
    lastPush.current = 0;
    setDirty(true);
  }, [put]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && tag !== 'input' && tag !== 'textarea') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
    };
    const onMsg = (e) => {
      if (e.data && 'gmBlock' in e.data) {
        setSel(e.data.gmBlock);
        setPanel(e.data.gmBlock ? 'block' : 'theme');
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('message', onMsg);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('message', onMsg); };
  }, [undo, redo]);

  const html = useMemo(() => (doc ? previewHtml(renderEmail(doc, { selectable: true, selected: sel, subject: meta.subject, preheader: meta.preheader }), SAMPLE) : ''), [doc, sel, meta.subject, meta.preheader]);

  // Önizleme her değişiklikte yeniden yüklenir; kaydırma konumunu koru
  const onFrameLoad = () => {
    const w = frame.current?.contentWindow;
    if (!w) return;
    w.scrollTo(0, scrollY.current);
    w.addEventListener('scroll', () => { scrollY.current = w.scrollY; });
  };

  const block = doc?.blocks.find((b) => b.id === sel) || null;
  const setBlock = (patch) => change((d) => { Object.assign(d.blocks.find((b) => b.id === sel), patch); });
  const move = (id, dir) => change((d) => {
    const i = d.blocks.findIndex((b) => b.id === id);
    const j = i + dir;
    if (j < 0 || j >= d.blocks.length) return;
    [d.blocks[i], d.blocks[j]] = [d.blocks[j], d.blocks[i]];
  });
  const dup = (id) => {
    const nid = uid();
    change((d) => { const i = d.blocks.findIndex((b) => b.id === id); d.blocks.splice(i + 1, 0, { ...clone(d.blocks[i]), id: nid }); });
    setSel(nid);
  };
  const del = (id) => { change((d) => { d.blocks = d.blocks.filter((b) => b.id !== id); }); if (sel === id) setSel(null); };
  const add = (type) => {
    const b = newBlock(type);
    change((d) => {
      const i = d.blocks.findIndex((x) => x.id === sel);
      d.blocks.splice(i >= 0 ? i + 1 : d.blocks.length, 0, b);
    });
    setSel(b.id);
    setPanel('block');
  };

  const tags = useMemo(() => (doc ? [...new Set(['first_name', ...usedTags(doc)])] : []), [doc]);
  const insertTag = (key) => {
    const f = focusRef.current;
    const tag = key === 'first_name' ? '{{first_name|Selam}}' : `{{${key}|}}`;
    if (!f?.el || !document.body.contains(f.el)) { navigator.clipboard?.writeText(tag); toast.success(`${tag} copied — click a text field first to insert`); return; }
    const { el } = f;
    const s = el.selectionStart ?? el.value.length;
    const e = el.selectionEnd ?? s;
    f.onChange(el.value.slice(0, s) + tag + el.value.slice(e));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + tag.length - (key === 'first_name' ? 0 : 2); });
  };

  const save = async () => {
    if (!meta.name.trim()) { toast.error('Template name is required'); setPanel('email'); return; }
    setSaving(true);
    try {
      const d = clone(doc);
      const need = collectImages(d);
      if (need.length) {
        const r = await mailApi(brandId, '/images', { method: 'POST', body: { items: need.map(({ url, w, crop }) => ({ url, w, crop })) } });
        let missing = 0;
        need.forEach((n) => { const u = r.images[`${n.url}|${n.w}|${n.crop}`]; if (u) n.apply(u); else missing += 1; });
        if (missing) toast.error(`${missing} image(s) could not be converted to JPG; they may not show in Outlook`);
      }
      const out = renderEmail(d, { subject: meta.subject, preheader: meta.preheader, campaign: slugify(meta.name) });
      const body = { name: meta.name, subject: meta.subject, preheader: meta.preheader, html: out, design: d };
      const r = meta.id
        ? await mailApi(brandId, `/templates/${meta.id}`, { method: 'PATCH', body })
        : await mailApi(brandId, '/templates', { method: 'POST', body });
      setDoc(d);
      setMeta((m) => ({ ...m, id: r.template.id }));
      setDirty(false);
      toast.success('Template saved');
      onSaved?.(r.template);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const close = () => { if (!dirty || window.confirm('Discard unsaved changes?')) onClose(); };

  if (!doc) {
    return (
      <StarterPicker brandId={brandId} theme={lastTheme} onClose={onClose} onPick={(s) => {
        const d = docFromStarter(s, brandId);
        if (lastTheme && s.key === 'blank') d.theme = clone(lastTheme);
        setDoc(d);
        setMeta((m) => ({ ...m, name: s.key === 'blank' ? 'Untitled email' : s.name, subject: s.subject, preheader: s.preheader }));
        setDirty(true);
      }} />
    );
  }

  const tab = (k, label, Icon) => (
    <button type="button" onClick={() => setPanel(k)} className={`flex-1 inline-flex items-center justify-center gap-1 py-2 text-[11px] border-b-2 ${panel === k ? 'border-indigo-600 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-gray-100 flex flex-col">
      {/* üst çubuk */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-center gap-3 px-4 shrink-0">
        <button className={btnGhost} onClick={close}><X />Close</button>
        <input className="text-sm font-medium text-gray-900 border-0 outline-none bg-transparent min-w-0 flex-1 max-w-md" value={meta.name} onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }} />
        <div className="flex-1" />
        <button className={btnGhost} onClick={undo} disabled={!hist.current.past.length} title="Undo (⌘Z)"><Undo2 /></button>
        <button className={btnGhost} onClick={redo} disabled={!hist.current.future.length} title="Redo (⇧⌘Z)"><Redo2 /></button>
        <Seg value={device} options={[['desktop', <Monitor key="d" className="w-3.5 h-3.5" />], ['mobile', <Smartphone key="m" className="w-3.5 h-3.5" />]]} onChange={setDevice} />
        <button className={btnPrimary} onClick={save} disabled={saving || (!dirty && meta.id)}>{saving ? <Loader2 className="animate-spin" /> : <Save />}{meta.id ? 'Save' : 'Save template'}</button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* sol: bloklar */}
        <div className="w-60 bg-white border-r border-gray-200 flex flex-col min-h-0 shrink-0">
          <div className="p-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Add block</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(BLOCK_TYPES).map(([k, v]) => {
                const I = ICONS[k];
                return <button key={k} type="button" title={v.hint} onClick={() => add(k)} className="flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1.5 text-[11px] text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"><I className="w-3.5 h-3.5 text-gray-400" />{v.label}</button>;
              })}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Blocks</div>
            <div className="text-[11px] text-gray-400 px-2 py-1">Header · logo + menu</div>
            {doc.blocks.map((b, i) => {
              const I = ICONS[b.type] || Type;
              return (
                <div key={b.id} onClick={() => { setSel(b.id); setPanel('block'); }}
                  className={`group flex items-center gap-1.5 rounded-md px-2 py-1.5 cursor-pointer ${sel === b.id ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'hover:bg-gray-50'}`}>
                  <I className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-medium text-gray-800">{BLOCK_TYPES[b.type]?.label}</div>
                    <div className="text-[10px] text-gray-500 truncate">{blockSummary(b) || '—'}</div>
                  </div>
                  <div className="hidden group-hover:flex items-center">
                    <button type="button" className="text-gray-400 hover:text-gray-800" disabled={i === 0} onClick={(e) => { e.stopPropagation(); move(b.id, -1); }}><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button type="button" className="text-gray-400 hover:text-gray-800" disabled={i === doc.blocks.length - 1} onClick={(e) => { e.stopPropagation(); move(b.id, 1); }}><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button type="button" className="text-gray-400 hover:text-gray-800" onClick={(e) => { e.stopPropagation(); dup(b.id); }}><Copy className="w-3 h-3" /></button>
                    <button type="button" className="text-gray-300 hover:text-red-600" onClick={(e) => { e.stopPropagation(); del(b.id); }}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              );
            })}
            <div className="text-[11px] text-gray-400 px-2 py-1">Footer · unsubscribe link</div>
          </div>
        </div>

        {/* orta: önizleme */}
        <div className="flex-1 overflow-hidden flex justify-center bg-[#e9e9e9]">
          <iframe ref={frame} title="preview" srcDoc={html} onLoad={onFrameLoad} sandbox="allow-scripts allow-same-origin"
            className="border-0 bg-[#e9e9e9] h-full transition-[width]" style={{ width: device === 'mobile' ? 390 : 680 }} />
        </div>

        {/* sağ: denetçi */}
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col min-h-0 shrink-0">
          <div className="flex border-b border-gray-100">
            {tab('block', 'Block', LayoutGrid)}
            {tab('theme', 'Brand', Palette)}
            {tab('email', 'Email', Mail)}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {panel === 'block' && (block
              ? <><div className="text-sm font-semibold text-gray-900 mb-3">{BLOCK_TYPES[block.type]?.label}</div>
                <BlockInspector b={block} set={setBlock} focusRef={focusRef} pick={(cb) => setPicker(() => cb)} /></>
              : <p className="text-xs text-gray-500">Click a block in the preview or the list to edit it. Header and footer come from the <b>Brand</b> tab.</p>)}
            {panel === 'theme' && <ThemeInspector t={doc.theme} focusRef={focusRef} set={(patch) => change((d) => { Object.assign(d.theme, patch); })} />}
            {panel === 'email' && (<>
              <Row label="Template name"><input className={inputCls} value={meta.name} onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }} /></Row>
              <Row label="Subject"><input className={inputCls} value={meta.subject} onChange={(e) => { setMeta({ ...meta, subject: e.target.value }); setDirty(true); }} /></Row>
              <Row label="Preview text" hint="Shown after the subject in the inbox"><input className={inputCls} value={meta.preheader} onChange={(e) => { setMeta({ ...meta, preheader: e.target.value }); setDirty(true); }} /></Row>
            </>)}
          </div>
          {/* kişi alanları */}
          <div className="border-t border-gray-100 p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Personal fields</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.map((k) => <button key={k} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertTag(k)} className="rounded-md bg-gray-100 hover:bg-indigo-100 px-1.5 py-0.5 text-[10px] font-mono text-gray-700">{k}</button>)}
            </div>
            <div className="flex gap-1">
              <input className={`${inputCls} !text-[11px]`} placeholder="new_field" value={customTag} onChange={(e) => setCustomTag(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} />
              <button type="button" className={btnGhost} disabled={!customTag} onMouseDown={(e) => e.preventDefault()} onClick={() => { insertTag(customTag); setCustomTag(''); }}>Insert</button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Filled per contact at send time. <span className="font-mono">{'{{field|fallback}}'}</span> shows the fallback when the contact has no value.</p>
          </div>
        </div>
      </div>

      {picker && <ProductPicker brandId={brandId} onClose={() => setPicker(null)} onPick={(item) => { picker(item); setPicker(null); }} />}
    </div>
  );
}
